<script setup lang="ts">
// Picks a build: from GET /builds (branch → artefact), or a URL with an optional sha256. Without one (and no
// checksum sidecar in the listing) the build is verified on first fetch: the operator computes its sha256 then and
// holds every later fetch of the URL to it (FR-11 amended 2026-10-05). peak-api never computes it (AD-17).
import { computed, onMounted, ref, watch } from 'vue';
import { must } from '../api/client';
import type { Build, BuildArtefact } from '../api/types';
import { useContext } from '../context';
import { errorsAt, type FieldError } from '../api/validate';
import ProblemAlert from './ProblemAlert.vue';
import { buildLabel, httpHref, offered, shortCommit } from '../forms/build';

// project: whose builds to list. The services' builds are one archive with no add-ons (plan.md M7): a zip, for
// contingentservice a tar.gz; the model is still a Build whose base is that archive, the caller sends base alone.
const props = defineProps<{ idPrefix: string; errors?: FieldError[]; pathPrefix?: string; project?: 'peaksolution' | 'cashdesk' | 'ticketservice' | 'contingentservice'; legend?: string }>();
const model = defineModel<Build | undefined>();

const { api } = useContext();
const mode = ref<'list' | 'manual'>('list');
const branches = ref<string[]>([]);
const branch = ref('');
// a native select's type-ahead only matches the start of a name, useless for "52008" in feature/PEAK-52008_…
const branchFilter = ref('');
const matches = computed(() => {
  const f = branchFilter.value.trim().toLowerCase();
  return f ? branches.value.filter((b) => b.toLowerCase().includes(f)) : branches.value;
});
// the chosen branch stays listed, so the select never holds a value it has no option for
const shownBranches = computed(() => (branch.value && !matches.value.includes(branch.value) ? [branch.value, ...matches.value] : matches.value));
watch(matches, (m) => {
  if (branchFilter.value.trim() && m.length === 1 && m[0]) branch.value = m[0];
});
const builds = ref<BuildArtefact[]>([]);
const truncated = ref(false);
const chosenUrl = ref('');
const manualUrl = ref('');
const sha256 = ref('');
const addonsText = ref('');
const loadError = ref<unknown>();
const loading = ref(false);
const project = computed(() => props.project ?? 'peaksolution');
const service = computed(() => project.value !== 'peaksolution');
const tarFile = computed(() => (project.value === 'contingentservice' ? 'contingentservice.tar.gz' : 'peaksolution.tar.gz'));
const zip = computed(() => service.value && project.value !== 'contingentservice');
const fileHint = computed(() => (zip.value ? `.zip, e.g. peak-${project.value}-play-<version>.zip` : `.tar.gz / .tgz, e.g. ${tarFile.value}`));
const placeholder = computed(() =>
  zip.value
    ? `https://minio2.alturos.com/artifacts/${project.value}/<branch>/<pipeline>/peak-${project.value}-play-<version>.zip`
    : `https://minio2.alturos.com/artifacts/${project.value}/<branch>/<pipeline>/${tarFile.value}`,
);

onMounted(async () => {
  try {
    loading.value = true;
    branches.value = must(await api.GET('/builds', { params: { query: { project: project.value } } })).branches;
  } catch (e) {
    loadError.value = e;
    mode.value = 'manual';
  } finally {
    loading.value = false;
  }
});

watch(branch, async (b) => {
  builds.value = [];
  chosenUrl.value = '';
  if (!b) return;
  try {
    loading.value = true;
    loadError.value = undefined;
    // a small page: releasemanager looks up each listed release's pipeline
    const list = must(await api.GET('/builds', { params: { query: { project: project.value, branch: b, limit: BUILDS_SHOWN } } }));
    builds.value = offered(list.items);
    truncated.value = !!list.truncated;
    if (builds.value[0]) chosenUrl.value = builds.value[0].url;
  } catch (e) {
    loadError.value = e;
  } finally {
    loading.value = false;
  }
});

const BUILDS_SHOWN = 25;
const chosen = computed(() => builds.value.find((b) => b.url === chosenUrl.value));
const knownSha = computed(() => (mode.value === 'list' ? chosen.value?.sha256 : undefined));

const addonsParse = computed(() => {
  const addons: { url: string; sha256?: string }[] = [];
  const bad: number[] = [];
  addonsText.value.split('\n').forEach((line, i) => {
    const t = line.trim();
    if (!t || t.startsWith('#')) return;
    const [url, sha, ...rest] = t.split(/\s+/);
    if (!url || rest.length) bad.push(i + 1);
    else addons.push(sha ? { url, sha256: sha } : { url });
  });
  return { addons, bad };
});

watch(
  [mode, chosenUrl, manualUrl, sha256, knownSha, addonsParse],
  () => {
    const url = mode.value === 'list' ? chosenUrl.value : manualUrl.value.trim();
    if (!url) { model.value = undefined; return; }
    const sha = knownSha.value ?? (sha256.value.trim() || undefined);
    const b: Build = { base: sha ? { url, sha256: sha } : { url } };
    if (!service.value && addonsParse.value.addons.length) b.addons = addonsParse.value.addons;
    model.value = b;
  },
  { immediate: true },
);

const p = computed(() => props.pathPrefix ?? 'build');
// a service's build is the artefact itself (cashdesk.build.url); peaksolution's is a base plus add-ons
const basePath = computed(() => (service.value ? p.value : `${p.value}.base`));
const urlErrors = computed(() => errorsAt(props.errors ?? [], `${basePath.value}.url`));
const shaErrors = computed(() => errorsAt(props.errors ?? [], `${basePath.value}.sha256`));
const baseErrors = computed(() => (props.errors ?? []).filter((e) => e.path === p.value || e.path === `${p.value}.base`));
const addonErrors = computed(() => errorsAt(props.errors ?? [], `${p.value}.addons`));

</script>

<template>
  <fieldset>
    <legend>{{ legend ?? 'Build' }}</legend>
    <div class="field inline" role="radiogroup" aria-label="How to choose the build">
      <label class="inline"><input v-model="mode" type="radio" value="list" :name="`${idPrefix}-mode`" /> From the build list</label>
      <label class="inline"><input v-model="mode" type="radio" value="manual" :name="`${idPrefix}-mode`" /> URL and sha256</label>
    </div>
    <ProblemAlert :error="loadError" context="Build list" />

    <template v-if="mode === 'list'">
      <div class="field">
        <label :for="`${idPrefix}-branch-filter`">Search branches</label>
        <input :id="`${idPrefix}-branch-filter`" v-model="branchFilter" type="search" spellcheck="false" autocomplete="off"
          placeholder="part of the name, e.g. 52008 or release-4-8" :aria-describedby="`${idPrefix}-branch-count`" />
        <p :id="`${idPrefix}-branch-count`" class="hint" data-testid="branch-count">
          <template v-if="branchFilter.trim()">{{ matches.length }} of {{ branches.length }} branches match</template>
          <template v-else>{{ branches.length }} branches, most recent build first</template>
        </p>
      </div>
      <div class="field">
        <label :for="`${idPrefix}-branch`">Branch</label>
        <select :id="`${idPrefix}-branch`" v-model="branch" :aria-busy="loading">
          <option value="">{{ branchFilter.trim() && !matches.length ? 'no branch matches' : '— choose a branch —' }}</option>
          <option v-for="b in shownBranches" :key="b" :value="b">{{ b }}</option>
        </select>
      </div>
      <div v-if="branch" class="field">
        <label :for="`${idPrefix}-build`">Build</label>
        <select :id="`${idPrefix}-build`" v-model="chosenUrl" :aria-invalid="urlErrors.length > 0" :aria-busy="loading">
          <option v-if="!builds.length" value="">{{ loading ? 'loading…' : 'no builds on this branch' }}</option>
          <option v-for="b in builds" :key="b.url" :value="b.url">{{ buildLabel(b) }}</option>
        </select>
        <p v-if="chosen?.release" class="hint" data-testid="build-release">
          {{ chosen.file }}<template v-if="chosen.release.commit"> · commit <code :title="chosen.release.commit">{{ shortCommit(chosen.release.commit) }}</code></template><template v-if="chosen.release.createdBy"> · by {{ chosen.release.createdBy }}</template><template v-if="chosen.release.pipelineStatus"> · pipeline <a v-if="httpHref(chosen.release.pipelineUrl)" :href="httpHref(chosen.release.pipelineUrl)" target="_blank" rel="noopener noreferrer">{{ chosen.release.pipelineStatus }}</a><template v-else>{{ chosen.release.pipelineStatus }}</template></template>
        </p>
        <p v-if="truncated" class="hint">Only the newest builds are listed.</p>
      </div>
    </template>
    <div v-else class="field">
      <label :for="`${idPrefix}-url`">Build URL ({{ fileHint }})</label>
      <input :id="`${idPrefix}-url`" v-model="manualUrl" type="text" :placeholder="placeholder" :aria-invalid="urlErrors.length > 0" :aria-describedby="`${idPrefix}-url-err`" />
      <p v-for="e in urlErrors" :id="`${idPrefix}-url-err`" :key="e.message" class="field-error">{{ e.message }}</p>
    </div>
    <p v-for="e in baseErrors" :key="e.message" class="field-error">Build {{ e.message }}</p>

    <div v-if="knownSha" class="field">
      <span class="label">sha256</span>
      <code>{{ knownSha }}</code> <span class="hint">(from the bucket's checksum file)</span>
    </div>
    <div v-else class="field">
      <label :for="`${idPrefix}-sha`">sha256 of the build (optional)</label>
      <input :id="`${idPrefix}-sha`" v-model="sha256" type="text" spellcheck="false" placeholder="64 hex characters" :aria-invalid="shaErrors.length > 0" :aria-describedby="`${idPrefix}-sha-hint`" />
      <p :id="`${idPrefix}-sha-hint`" class="hint">Leave it empty to have the build <strong>verified on first fetch</strong>: its sha256 is computed when it is first fetched, and every later fetch of this URL must match it. Give it (e.g. <code>sha256sum peaksolution.tar.gz</code>) to have it verified against that instead.</p>
      <p v-for="e in shaErrors" :key="e.message" class="field-error">sha256 {{ e.message }}</p>
    </div>

    <div v-if="!service" class="field">
      <label :for="`${idPrefix}-addons`">Add-ons (optional)</label>
      <textarea :id="`${idPrefix}-addons`" v-model="addonsText" rows="2" spellcheck="false" placeholder="one per line: <url> [sha256]" :aria-invalid="addonErrors.length + addonsParse.bad.length > 0"></textarea>
      <p v-if="addonsParse.bad.length" class="field-error">Line {{ addonsParse.bad.join(', ') }}: expected "&lt;url&gt; [sha256]".</p>
      <p v-for="e in addonErrors" :key="e.path + e.message" class="field-error">{{ e.path.replace(`${p}.`, '') }} {{ e.message }}</p>
    </div>
  </fieldset>
</template>
